import React, { useEffect, useState, useCallback } from "react";
import { Heart } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Premium heart toggle for favoriting a coach.
 *  - Optimistic update with rollback on failure
 *  - Hidden for guests + the coach's own card
 *  - Variants: "icon" (default, small circular pill), "chip" (with label)
 */
export default function FavoriteButton({ coachId, variant = "icon", className = "", onChange }) {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || user.id === coachId) { setMounted(true); return; }
    (async () => {
      try {
        const { data } = await api.get("/favorites/ids");
        if (!cancelled) setActive((data.ids || []).includes(coachId));
      } catch (_) { /* anon */ }
      if (!cancelled) setMounted(true);
    })();
    return () => { cancelled = true; };
  }, [coachId, user]);

  const toggle = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || !user) return;
    const next = !active;
    setActive(next);
    setBusy(true);
    try {
      if (next) await api.post(`/favorites/${coachId}`);
      else await api.delete(`/favorites/${coachId}`);
      onChange && onChange(next);
    } catch (_) {
      setActive(!next); // rollback
    } finally {
      setBusy(false);
    }
  }, [active, busy, coachId, user, onChange]);

  if (!user || user.id === coachId || !mounted) return null;

  if (variant === "chip") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest border transition-all rounded-full ${active ? "bg-pitch/15 border-pitch/50 text-pitch" : "border-white/15 text-white/70 hover:border-pitch/40 hover:text-white"} ${className}`}
        data-testid={`fav-chip-${coachId}`}
        aria-pressed={active}
        aria-label={active ? "Remove from favorites" : "Add to favorites"}
      >
        <Heart className={`w-3.5 h-3.5 ${active ? "fill-pitch text-pitch" : ""}`} />
        <span>{active ? "Saved" : "Save"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all backdrop-blur-md ${active ? "bg-pitch/15 border-pitch/55 text-pitch shadow-[0_0_18px_rgba(59,130,246,0.25)]" : "bg-black/30 border-white/15 text-white/70 hover:border-pitch/50 hover:text-pitch"} ${className}`}
      data-testid={`fav-btn-${coachId}`}
      aria-pressed={active}
      aria-label={active ? "Remove from favorites" : "Add to favorites"}
      title={active ? "Saved" : "Save coach"}
    >
      <Heart className={`w-4 h-4 transition-transform duration-200 ${active ? "fill-pitch scale-110" : ""}`} />
    </button>
  );
}
